import { Controller } from "@hotwired/stimulus"

const PANEL_INSET = 8
const PANEL_TOP_INSET = 6
const PANEL_HEIGHT = 405
const PANEL_EXPANDED_HEIGHT = 505
const PANEL_CLOSE_FALLBACK_MS = 250
const VIEWPORT_MARGIN = 8
const EXPANSION_RIGHT_MARGIN = 16

export default class extends Controller {
  static targets = ["input", "panel", "bar"]

  connect() {
    this.isOpen = false
    this.panelTarget.hidden = true
    this.element.classList.remove("header__search--open")
  }

  disconnect() {
    this.close()
  }

  open() {
    if (this.isOpen) return

    this.isOpen = true
    this.cancelPanelHide()
    this.element.classList.add("header__search--open")
    this.panelTarget.hidden = false
    this.startPositioning()
    document.addEventListener("click", this.handleDocumentClick)
    // Capture phase so an open scope menu is still visible when we check;
    // the dropdown controller's bubble-phase Escape handler runs first
    // otherwise and hides the menu before we can tell it was open.
    document.addEventListener("keydown", this.handleDocumentKeydown, true)
    this.element.addEventListener("keydown", this.handleTrapKeydown)
  }

  close() {
    if (!this.isOpen) return

    this.isOpen = false
    this.element.classList.remove("header__search--open")
    this.hidePanel()
    this.stopPositioning()
    document.removeEventListener("click", this.handleDocumentClick)
    document.removeEventListener("keydown", this.handleDocumentKeydown, true)
    this.element.removeEventListener("keydown", this.handleTrapKeydown)
  }

  hidePanel() {
    const panel = this.panelTarget
    panel.classList.add("header__search-panel--closing")
    this.panelHideHandler = () => {
      clearTimeout(this.panelHideTimeout)
      this.panelHideTimeout = null
      panel.removeEventListener("animationend", this.panelHideHandler)
      panel.classList.remove("header__search-panel--closing")
      panel.hidden = true
      this.panelHideHandler = null
      this.dispatch("closed")
    }
    panel.addEventListener("animationend", this.panelHideHandler, { once: true })
    this.panelHideTimeout = setTimeout(this.panelHideHandler, PANEL_CLOSE_FALLBACK_MS)
  }

  cancelPanelHide() {
    if (!this.panelHideHandler) return

    clearTimeout(this.panelHideTimeout)
    this.panelHideTimeout = null
    this.panelTarget.removeEventListener("animationend", this.panelHideHandler)
    this.panelTarget.classList.remove("header__search-panel--closing")
    this.panelHideHandler = null
  }

  focusInput() {
    this.inputTarget.focus()
  }

  handleFocusOut(event) {
    if (event.relatedTarget && !this.element.contains(event.relatedTarget)) {
      this.close()
    }
  }

  handleDocumentClick = (event) => {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  handleDocumentKeydown = (event) => {
    if (event.key !== "Escape") return
    if (this.element.querySelector(".dropdown__content:not(.hidden)")) return

    event.preventDefault()
    this.close()
    this.inputTarget.blur()
  }

  // Keep Tab within the open panel (Esc is the documented way out). Without
  // this, tabbing past the last result leaks focus into the header icons
  // behind the panel.
  handleTrapKeydown = (event) => {
    if (event.key !== "Tab") return
    // An open scope menu runs its own focus cycle; don't fight it.
    if (this.element.querySelector(".dropdown__content:not(.hidden)")) return

    const focusables = this.focusableElements()
    if (focusables.length === 0) return

    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    const active = document.activeElement

    if (event.shiftKey && active === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && active === last) {
      event.preventDefault()
      first.focus()
    }
  }

  focusableElements() {
    const selector = "a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex='-1'])"
    return Array.from(this.element.querySelectorAll(selector)).filter(el => this.isVisible(el))
  }

  isVisible(el) {
    if (el.closest("[hidden]")) return false
    if (el.style.display === "none") return false
    return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement
  }

  startPositioning() {
    this.stopPositioning()
    this.lastPanelHeight = null
    this.lastBarRect = null
    this.panelChrome = null
    const tick = () => {
      this.updateExpansionLimit()
      this.positionPanel()
      this.updatePanelHeight()
      this.positioningFrame = requestAnimationFrame(tick)
    }
    tick()
  }

  // CSS can't know the bar's left offset, so the max expanded width that
  // keeps the bar on screen is published as a custom property for the
  // open-state width rule
  updateExpansionLimit() {
    const available = Math.max(0, Math.round(this.viewportWidth() -
      this.element.getBoundingClientRect().left - EXPANSION_RIGHT_MARGIN))
    if (available === this.lastExpansionLimit) return

    this.lastExpansionLimit = available
    this.element.style.setProperty("--header-search-open-max", `${available}px`)
  }

  // The panel hugs the bar with an inset, clamped so it never leaves the
  // viewport (the mobile takeover runs the bar nearly edge to edge)
  positionPanel() {
    const rect = this.barTarget.getBoundingClientRect()
    const last = this.lastBarRect
    if (last && rect.left === last.left && rect.top === last.top && rect.width === last.width) return

    this.lastBarRect = rect
    const viewportWidth = this.viewportWidth()
    const left = Math.max(VIEWPORT_MARGIN, rect.left - PANEL_INSET)
    const width = Math.max(0, Math.min(rect.width + PANEL_INSET * 2, viewportWidth - VIEWPORT_MARGIN - left))
    Object.assign(this.panelTarget.style, {
      left: `${left}px`,
      top: `${rect.top - PANEL_TOP_INSET}px`,
      width: `${width}px`,
    })
  }

  updatePanelHeight() {
    if (this.panelIsLoading()) return

    const expand = this.panelHasResults() && this.panelContentHeight() > PANEL_HEIGHT
    const height = expand ? PANEL_EXPANDED_HEIGHT : PANEL_HEIGHT
    if (height === this.lastPanelHeight) return

    this.lastPanelHeight = height
    this.panelTarget.style.height = `${height}px`
  }

  panelIsLoading() {
    const results = this.visibleResults()
    return Boolean(results) && results.querySelector(".searcher__loading") !== null
  }

  panelHasResults() {
    const results = this.visibleResults()
    return Boolean(results) && results.querySelector(".searcher__result") !== null
  }

  visibleResults() {
    const results = this.panelTarget.querySelector(".searcher__results")
    return results && results.style.display !== "none" ? results : null
  }

  panelContentHeight() {
    if (this.panelChrome == null) {
      const styles = getComputedStyle(this.panelTarget)
      this.panelChrome = (parseFloat(styles.paddingTop) || 0) +
        (parseFloat(styles.borderTopWidth) || 0) +
        (parseFloat(styles.borderBottomWidth) || 0)
    }

    return Array.from(this.panelTarget.children).reduce((sum, child) => {
      if (child.classList.contains("searcher__results")) {
        return sum + Array.from(child.children).reduce((s, section) => s + section.offsetHeight, 0)
      }
      return sum + child.offsetHeight
    }, this.panelChrome)
  }

  // clientWidth: fixed-position coordinates exclude the scrollbar,
  // window.innerWidth does not (fallback for environments without layout)
  viewportWidth() {
    return document.documentElement.clientWidth || window.innerWidth
  }

  stopPositioning() {
    if (this.positioningFrame) {
      cancelAnimationFrame(this.positioningFrame)
      this.positioningFrame = null
    }
  }
}
